// src/pages/agenda/Detalhes.js — o compromisso selecionado.
//
// Fiel ao design: selo do tipo + selo do status, título, ações principais
// e as linhas Quando / Contato / Responsável… Ações que o design não
// desenhou (confirmar, não compareceu, concluir, cancelar) ficam numa
// linha discreta de texto embaixo — sem elas a agenda seria só leitura.
//
// Cancelar e remover pedem um segundo clique: a ação não tem desfazer.
//
// Fase 3: a proposta do Copiloto (tracejada) também abre aqui, com
// "Confirmar …" e "Descartar" — o mesmo clique do card do Copiloto, e a
// mesma rota (/copilot/actions/{id}/confirm), que refaz as conferências.
import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { FiMessageCircle } from 'react-icons/fi';
import Faisca from '../../components/copiloto/Faisca';
import { add, diaCurto } from './datas';
import {
  FONT_TXT, LILAS, TIPOS, botaoBase, botaoPrimario, botaoSecundario, cor, faixa, rotuloSecao, tomStatus, umTipo,
} from './estilo';

const linkAcao = (perigo) => ({
  ...botaoBase, border: 'none', bgcolor: 'transparent', p: '4px 0', fontSize: '0.7rem', fontWeight: 600,
  color: perigo ? 'var(--ch-red)' : 'var(--ch-text2)', textDecoration: 'underline', textUnderlineOffset: '3px',
  textDecorationColor: 'var(--ch-bdr2)', '&:hover': { color: perigo ? 'var(--ch-red)' : 'var(--ch-text)' },
  '@media (pointer: coarse)': { minHeight: 40, px: '4px' },
});
const toque = { '@media (pointer: coarse)': { minHeight: 40 } };

function linhas(ev) {
  const d = ev.disparo;
  const r = [];
  if (ev.tipo === 'consulta') {
    if (ev.contato) r.push({ k: 'Contato', v: ev.contato.nome, sub: ev.contato.fone });
    r.push({ k: 'Responsável', v: ev.responsavel?.nome || 'Sem responsável' });
    if (ev.origem_txt) r.push({ k: 'Origem', v: ev.origem_txt });
  } else if (ev.tipo === 'disparo') {
    r.push({ k: 'Template', v: d.template, sub: 'Template da Meta' });
    r.push({ k: 'Destino', v: `${d.contatos} contato${d.contatos === 1 ? '' : 's'}`, sub: d.pasta ? `Pasta ${d.pasta}` : 'Contatos escolhidos' });
    r.push({ k: 'Chip', v: d.chip });
    if (ev.responsavel) r.push({ k: 'Criado por', v: ev.responsavel.nome });
  } else {
    if (ev.contato) r.push({ k: 'Contato', v: ev.contato.nome, sub: ev.contato.fone });
    r.push({ k: 'Responsável', v: ev.responsavel?.nome || 'Sem responsável' });
    if (ev.nota) r.push({ k: 'Nota', v: ev.nota });
  }
  if (ev.tipo === 'consulta' && ev.nota) r.push({ k: 'Nota', v: ev.nota });
  return r;
}

// Fase 2: o lembrete automático ao cliente desta consulta — o que vai sair,
// o que saiu e o que não saiu (com o motivo, pra quem precisa agir).
const TOM_LEMBRETE = {
  pendente: 'var(--ch-text2)', enviando: 'var(--ch-text2)', enviado: 'var(--ch-green-d)',
  falhou: 'var(--ch-red)', pulado: 'var(--ch-text3)', cancelado: 'var(--ch-text3)',
};

function LembreteCliente({ ev, ativo }) {
  const itens = ev.lembretes_cliente || [];
  let vazio = null;
  if (!itens.length) {
    if (ev.avisar_cliente === false) vazio = 'Desligado neste horário.';
    else if (ev.status_tom === 'neutro' || ev.status === 'nao_compareceu') vazio = null;
    else vazio = 'Nenhum programado — o horário está perto demais para as antecedências configuradas.';
  }
  if (!itens.length && !vazio) return null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', borderTop: '1px solid var(--ch-bdr)', pt: '8px' }}>
      <Box component="span" sx={{ fontSize: '0.6rem', color: 'var(--ch-text3)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        Lembrete ao cliente
      </Box>
      {vazio && <Box component="span" sx={{ fontSize: '0.7rem', color: 'var(--ch-text2)', lineHeight: 1.45 }}>{vazio}</Box>}
      {itens.map((l, i) => (
        <Box key={`${l.antecedencia}-${i}`} sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', fontSize: '0.72rem' }}>
            <Box component="span" sx={{ color: 'var(--ch-text)', fontWeight: 600, whiteSpace: 'nowrap' }}>{l.antecedencia}</Box>
            <Box component="span" sx={{ color: TOM_LEMBRETE[l.status] || 'var(--ch-text2)', fontWeight: 700, whiteSpace: 'nowrap' }}>{l.status_txt}</Box>
          </Box>
          <Box component="span" sx={{ fontSize: '0.66rem', color: 'var(--ch-text2)', fontVariantNumeric: 'tabular-nums' }}>
            {l.status === 'enviado' ? `Enviado em ${l.quando}` : l.status === 'pendente' ? `Sai em ${l.quando}` : `Previsto para ${l.quando}`}
          </Box>
          {l.erro && l.status !== 'enviado' && (
            <Box component="span" sx={{ fontSize: '0.66rem', color: 'var(--ch-text2)', lineHeight: 1.4, overflowWrap: 'anywhere' }}>{l.erro}</Box>
          )}
        </Box>
      ))}
      {!ativo && itens.length > 0 && (
        <Box component="span" sx={{ fontSize: '0.66rem', color: 'var(--ch-text3)' }}>O lembrete automático está desligado na empresa.</Box>
      )}
    </Box>
  );
}

function Proposta({ ev, nome, ocupado, erro, onConfirmar, onDescartar }) {
  const p = ev.proposta;
  const k = cor(TIPOS[ev.tipo].c);
  const st = tomStatus('lilas');
  const titulo = ev.tipo === 'consulta' ? `${umTipo('consulta', nome)} com ${ev.titulo}` : ev.titulo;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.66rem', fontWeight: 700, p: '3px 8px', borderRadius: '6px', color: k.d, bgcolor: k.bg, border: `1px solid ${k.b}` }}>
          <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: k.base }} />{umTipo(ev.tipo, nome)}
        </Box>
        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.66rem', fontWeight: 700, p: '3px 8px', borderRadius: '6px', color: st.cor, bgcolor: st.bg, border: `1px solid ${st.b}` }}>
          <Faisca size={10} color={LILAS} />{p.operacao === 'remarcar' ? 'Remarcação proposta' : 'Proposta do Copiloto'}
        </Box>
      </Box>
      <Box component="h3" sx={{ m: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--ch-text)', lineHeight: 1.25, textWrap: 'pretty', overflowWrap: 'anywhere' }}>{titulo}</Box>
      {/* Na coluna de 272px os dois não cabem lado a lado sem quebrar o
          texto do botão: aí cada um ocupa a linha inteira. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        <Box component="button" type="button" disabled={ocupado} onClick={() => onConfirmar(ev)}
          sx={{ ...botaoPrimario, ...toque, flex: '1 1 150px', whiteSpace: 'nowrap' }}>
          {p.rotulo_botao || 'Confirmar'}
        </Box>
        <Box component="button" type="button" disabled={ocupado} onClick={() => onDescartar(ev)}
          sx={{ ...botaoSecundario, ...toque, flex: '1 1 auto', whiteSpace: 'nowrap' }}>Descartar</Box>
      </Box>
      <Box component="span" sx={{ fontSize: '0.68rem', color: 'var(--ch-text2)', lineHeight: 1.45, mt: '-4px' }}>
        {p.operacao === 'remarcar'
          ? 'O horário atual continua valendo até você confirmar.'
          : 'Nada é marcado nem enviado ao cliente antes da sua confirmação.'}
      </Box>
      {erro && <Box role="alert" sx={{ fontSize: '0.7rem', color: 'var(--ch-red)', lineHeight: 1.45 }}>{erro}</Box>}
      {(p.conflitos || []).length > 0 && (
        <Box sx={{ fontSize: '0.7rem', color: 'var(--ch-amber-d)', bgcolor: 'var(--ch-amber-bg)', border: '1px solid var(--ch-amber-b)', borderRadius: '6px', p: '6px 8px', lineHeight: 1.45 }}>
          Conflito: {p.conflitos.map((c) => `${c.titulo} ${c.ini}–${c.fim}`).join(', ')}. A agenda permite, mas confira.
        </Box>
      )}
      {p.citacao && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', p: '7px 10px', borderRadius: '7px', bgcolor: 'var(--ch-inset)' }}>
          <Box component="span" sx={{ fontSize: '0.6rem', color: 'var(--ch-text3)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Na conversa</Box>
          <Box component="span" sx={{ fontSize: '0.74rem', color: 'var(--ch-text)', lineHeight: 1.45, overflowWrap: 'anywhere' }}>“{p.citacao.texto}”</Box>
          <Box component="span" sx={{ fontSize: '0.66rem', color: 'var(--ch-text2)' }}>{p.citacao.autor} · {p.citacao.quando}</Box>
        </Box>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {[...(p.linhas || []), { k: 'Origem', v: 'Proposto pelo Copiloto', sub: 'Só você vê até confirmar' }].map((r) => (
          <Box key={r.k} sx={{ display: 'grid', gridTemplateColumns: '98px minmax(0, 1fr)', gap: '8px', alignItems: 'baseline', py: '5px', borderTop: '1px solid var(--ch-bdr)' }}>
            <Box component="span" sx={{ fontSize: '0.6rem', color: 'var(--ch-text3)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{r.k}</Box>
            <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <Box component="span" sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--ch-text)', overflowWrap: 'anywhere' }}>{r.v}</Box>
              {r.sub && <Box component="span" sx={{ fontSize: '0.66rem', color: 'var(--ch-text2)', overflowWrap: 'anywhere' }}>{r.sub}</Box>}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function Detalhes({
  ev, nome, hoje, ocupado, erro, onAbrirConversa, onEditar, onRemarcar, onStatus, onConcluir, onRemover,
  onCancelarDisparo, onAbrirDisparos, semCartao, lembreteAtivo, onConfirmarProposta, onDescartarProposta,
}) {
  const [confirmando, setConfirmando] = useState(null);
  useEffect(() => { setConfirmando(null); }, [ev?.id]);

  const corpo = !ev ? (
    <Box component="span" sx={{ fontSize: '0.72rem', color: 'var(--ch-text2)', lineHeight: 1.5 }}>
      Selecione um compromisso na agenda para ver os detalhes.
    </Box>
  ) : ev.proposta ? (
    <Proposta ev={ev} nome={nome} ocupado={ocupado} erro={erro}
      onConfirmar={onConfirmarProposta} onDescartar={onDescartarProposta} />
  ) : (() => {
    const k = cor(TIPOS[ev.tipo].c);
    const st = tomStatus(ev.status_tom);
    const quandoSub = ev.data === hoje ? 'Hoje' : ev.data === add(hoje, 1) ? 'Amanhã' : '';
    const rows = [{ k: 'Quando', v: `${diaCurto(ev.data)} · ${faixa(ev)}`, sub: quandoSub }, ...linhas(ev)];
    const titulo = ev.tipo === 'consulta' ? `${umTipo('consulta', nome)} com ${ev.titulo}` : ev.titulo;
    const podeConversar = !!ev.contato?.device_id;
    const pedir = (acao) => (confirmando === acao ? (setConfirmando(null), true) : (setConfirmando(acao), false));

    let principais = null;
    const secundarias = [];
    if (ev.tipo === 'consulta') {
      principais = (
        <Box sx={{ display: 'flex', gap: '8px' }}>
          <Box component="button" type="button" disabled={!podeConversar || ocupado} onClick={() => onAbrirConversa(ev)}
            title={podeConversar ? undefined : 'Ainda não há conversa com este contato'}
            sx={{ ...botaoBase, ...toque, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '0.74rem', fontWeight: 700,
              color: 'var(--ch-green-d)', bgcolor: 'var(--ch-green-bg)', border: '1px solid var(--ch-green-b)', borderRadius: '6px', p: '8px 12px',
              '&:hover': { borderColor: 'var(--ch-green)' } }}>
            <FiMessageCircle size={13} /> Abrir conversa
          </Box>
          <Box component="button" type="button" disabled={ocupado} onClick={() => onRemarcar(ev)} sx={{ ...botaoSecundario, ...toque }}>Remarcar</Box>
        </Box>
      );
      secundarias.push(['Editar', () => onEditar(ev)]);
      if (ev.status === 'pendente') secundarias.push(['Confirmar', () => onStatus(ev, 'confirmada')]);
      if (ev.status === 'confirmada') secundarias.push(['Marcar como pendente', () => onStatus(ev, 'pendente')]);
      if (ev.status === 'realizada') secundarias.push(['Não compareceu', () => onStatus(ev, 'nao_compareceu')]);
      if (ev.status === 'nao_compareceu') secundarias.push(['Desfazer "não compareceu"', () => onStatus(ev, 'confirmada')]);
      secundarias.push([confirmando === 'remover' ? 'Confirmar cancelamento' : 'Cancelar', () => pedir('remover') && onRemover(ev), true]);
    } else if (ev.tipo === 'disparo') {
      principais = (
        <Box component="button" type="button" onClick={() => onAbrirDisparos(ev)} sx={{ ...botaoSecundario, ...toque }}>
          Abrir em Disparos agendados
        </Box>
      );
      if (ev.disparo.pode_cancelar) {
        secundarias.push([confirmando === 'disparo' ? 'Confirmar: cancelar disparo' : 'Cancelar disparo', () => pedir('disparo') && onCancelarDisparo(ev), true]);
      }
    } else {
      const feito = ev.status === 'concluido';
      principais = (
        <Box sx={{ display: 'flex', gap: '8px' }}>
          <Box component="button" type="button" disabled={ocupado} onClick={() => onConcluir(ev, !feito)}
            sx={{ ...(feito ? botaoSecundario : botaoPrimario), ...toque, flex: 1 }}>
            {feito ? 'Reabrir' : 'Concluir'}
          </Box>
          <Box component="button" type="button" disabled={ocupado} onClick={() => onEditar(ev)} sx={{ ...botaoSecundario, ...toque }}>Editar</Box>
        </Box>
      );
      if (podeConversar) secundarias.push(['Abrir conversa', () => onAbrirConversa(ev)]);
      secundarias.push([confirmando === 'remover' ? 'Confirmar: remover' : 'Remover', () => pedir('remover') && onRemover(ev), true]);
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.66rem', fontWeight: 700, p: '3px 8px', borderRadius: '6px', color: k.d, bgcolor: k.bg, border: `1px solid ${k.b}` }}>
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: k.base }} />{umTipo(ev.tipo, nome)}
          </Box>
          <Box component="span" sx={{ fontSize: '0.66rem', fontWeight: 700, p: '3px 8px', borderRadius: '6px', color: st.cor, bgcolor: st.bg, border: `1px solid ${st.b}` }}>
            {ev.status_txt}
          </Box>
        </Box>
        <Box component="h3" sx={{ m: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--ch-text)', lineHeight: 1.25, textWrap: 'pretty', overflowWrap: 'anywhere' }}>{titulo}</Box>
        {principais}
        {secundarias.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '2px 14px', mt: '-4px' }}>
            {secundarias.map(([rotulo, fn, perigo]) => (
              <Box key={rotulo} component="button" type="button" disabled={ocupado} onClick={fn} sx={linkAcao(perigo)}>{rotulo}</Box>
            ))}
          </Box>
        )}
        {erro && <Box role="alert" sx={{ fontSize: '0.7rem', color: 'var(--ch-red)', lineHeight: 1.45 }}>{erro}</Box>}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {rows.map((r) => (
            <Box key={r.k} sx={{ display: 'grid', gridTemplateColumns: '98px minmax(0, 1fr)', gap: '8px', alignItems: 'baseline', py: '5px', borderTop: '1px solid var(--ch-bdr)' }}>
              <Box component="span" sx={{ fontSize: '0.6rem', color: 'var(--ch-text3)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{r.k}</Box>
              <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Box component="span" sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--ch-text)', overflowWrap: 'anywhere', whiteSpace: 'pre-line' }}>{r.v}</Box>
                {r.sub && <Box component="span" sx={{ fontSize: '0.66rem', color: 'var(--ch-text2)' }}>{r.sub}</Box>}
              </Box>
            </Box>
          ))}
        </Box>
        {ev.tipo === 'consulta' && (lembreteAtivo || (ev.lembretes_cliente || []).length > 0) && (
          <LembreteCliente ev={ev} ativo={lembreteAtivo} />
        )}
      </Box>
    );
  })();

  if (semCartao) return <Box sx={{ fontFamily: FONT_TXT }}>{corpo}</Box>;
  return (
    <Box sx={{ bgcolor: 'var(--ch-card)', border: '1px solid var(--ch-bdr)', borderRadius: '12px', boxShadow: 'var(--sh-sm)', flex: 'none', p: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <Box component="span" sx={rotuloSecao}>Detalhes</Box>
      {corpo}
    </Box>
  );
}
